import { useCallback, useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { COMMENT_MAX_LENGTH, commentsApi, type CommentView } from "../api/comments";
import type { AttachmentEntityKind } from "../api/attachments";
import type { ApiError } from "../api/client";
import { formatApiError } from "../lib/apiError";
import { formatDateTime, formatRelativeSr } from "../lib/dates";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "./Toast";
import { useConfirm } from "./ConfirmDialog";
import Ic from "./icons.jsx";

const PAGE_SIZE = 20;

/**
 * Free-text notes on an entity (contract §6): newest first, relative time with
 * the exact stamp in `title`, body with preserved line breaks, add form gated
 * by `comments:create`, delete only where the server says `canDelete`.
 */
export function CommentsPanel({
  entityKind, entityId, title, placeholder,
}: {
  entityKind: AttachmentEntityKind;
  entityId: string;
  /** Card heading; defaults to "Napomene". */
  title?: string;
  /** Textarea hint; defaults to a maintenance-flavoured one, callers pass entity-specific text. */
  placeholder?: string;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("comments:create");
  const canView = hasPermission("comments:view");

  const [comments, setComments] = useState<CommentView[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadPage = useCallback(
    (p: number) => commentsApi.list({ entityKind, entityId, page: p, size: PAGE_SIZE }),
    [entityKind, entityId],
  );

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    setComments(null);
    setError(null);
    setPage(0);
    setBody("");
    loadPage(0)
      .then((res) => { if (!cancelled) { setComments(res.content); setTotal(res.totalElements); } })
      .catch((err: ApiError) => { if (!cancelled) { setError(err.message); setComments([]); } });
    return () => { cancelled = true; };
  }, [loadPage, canView]);

  async function loadMore() {
    setLoadingMore(true);
    try {
      const res = await loadPage(page + 1);
      setPage(page + 1);
      setComments((prev) => [...(prev ?? []), ...res.content]);
      setTotal(res.totalElements);
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setLoadingMore(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    setSubmitting(true);
    try {
      const created = await commentsApi.create({ entityKind, entityId, body: text });
      setComments((prev) => [created, ...(prev ?? [])]);
      setTotal((n) => n + 1);
      setBody("");
    } catch (err) {
      toast.error(formatApiError(err as ApiError));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(c: CommentView) {
    const ok = await confirm({
      title: t("comments.confirmDelete.title", "Brisanje napomene"),
      message: t("comments.confirmDelete.message", "Obrisati napomenu ({{author}}, {{when}})?", { author: c.authorName, when: formatDateTime(c.createdAt) }),
      confirmLabel: t("action.delete", "Obriši"),
      danger: true,
    });
    if (!ok) return;
    try {
      await commentsApi.remove(c.id);
      setComments((prev) => (prev ?? []).filter((x) => x.id !== c.id));
      setTotal((n) => Math.max(0, n - 1));
      toast.success(t("comments.toast.deleted", "Napomena obrisana."));
    } catch (err) {
      const ae = err as ApiError;
      toast.error(ae.status === 403 ? t("comments.error.forbidden", "Možete obrisati samo sopstvene napomene.") : ae.message);
    }
  }

  const loaded = comments?.length ?? 0;

  // Without the view function the whole card stays out — the page does not need to know (audit 2026-09-23 §C).
  if (!canView) return null;

  return (
    <section className="card">
      <div className="card-h">
        <div>
          <h3>{title ?? t("comments.title", "Napomene")}</h3>
          <div className="sub">
            {comments === null ? t("common.loading", "Učitavanje…") : t("comments.count", "{{count}} napomena", { count: total })}
          </div>
        </div>
      </div>

      {canWrite && (
        <form className="card-b comment-form" onSubmit={handleSubmit}>
          <label className="field" style={{ flex: 1 }}>
            <span className="sr-only">{t("comments.field.body", "Nova napomena")}</span>
            <textarea
              className="input"
              rows={3}
              value={body}
              maxLength={COMMENT_MAX_LENGTH}
              placeholder={placeholder ?? t("comments.placeholder", "Napišite napomenu — šta je urađeno, na šta paziti, gde je dokumentacija…")}
              onChange={(e) => setBody(e.target.value)}
              style={textarea}
            />
          </label>
          <div className="form-actions" style={{ justifyContent: "space-between", alignItems: "center" }}>
            <span className="muted" style={{ fontSize: 12 }}>{body.length}/{COMMENT_MAX_LENGTH}</span>
            <button type="submit" className="btn primary" disabled={submitting || !body.trim()}>
              <Ic.plus /> {submitting ? t("action.saving", "Čuvanje…") : t("comments.add", "Dodaj napomenu")}
            </button>
          </div>
        </form>
      )}

      {error ? (
        <div style={{ ...errorBox, margin: 18 }} role="alert">{error}</div>
      ) : comments === null ? (
        <div className="empty">{t("common.loading", "Učitavanje…")}</div>
      ) : comments.length === 0 ? (
        <div className="empty">{t("comments.empty", "Još nema napomena.")}</div>
      ) : (
        <div className="card-b" style={{ paddingTop: canWrite ? 0 : undefined }}>
          <ul className="comment-list">
            {comments.map((c) => (
              <li key={c.id} className="comment">
                <div className="comment-meta">
                  <b>{c.authorName}</b>
                  <time dateTime={c.createdAt} title={formatDateTime(c.createdAt)}>{formatRelativeSr(c.createdAt)}</time>
                  {c.canDelete && (
                    <button type="button" className="link-btn danger" onClick={() => handleDelete(c)}>
                      {t("action.delete", "Obriši")}
                    </button>
                  )}
                </div>
                <div className="comment-body">{c.body}</div>
              </li>
            ))}
          </ul>
          {loaded < total && (
            <div className="form-actions" style={{ justifyContent: "center", marginTop: 12 }}>
              <button type="button" className="btn sm" disabled={loadingMore} onClick={loadMore}>
                {loadingMore ? t("common.loading", "Učitavanje…") : t("comments.loadMore", "Učitaj još ({{count}})", { count: total - loaded })}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const textarea: CSSProperties = { resize: "vertical", fontFamily: "inherit", width: "100%" };
